import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc } from '@if/core/date'
import {
  EFinancingProgram,
  EPaymentFrequency,
  EPaymentPlan,
  EPlanType,
  EProvince,
  EWorksheetStatus,
} from '@if/core/types'
import { formatCurrency, formatDate, normalizeNumber } from '@if/core/utils'
import { ControlledStringDateOnlyPicker } from '@if/mui'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Stack,
  Typography,
} from '@mui/material'
import {
  useMaxPaymentAmountWithAgentLeeway,
  useMaxPaymentError
} from '@src/containers/ViewWorksheet/viewWorksheet-hooks'
import {
  canComputeFunding,
  getEmptyComputedFundingDto,
  getHighCostRateForProvince,
} from '@src/containers/ViewWorksheet/viewWorksheet-selectors'
import { useCreditApplicationContext, useCreditApplicationData } from '@src/contexts/CreditApplicationContext'
import { useGetAvailableCredit } from '@src/data/api/available-credit-api'
import { useMerchantFinancingConfig } from '@src/data/api/config/financing-config-api'
import { useInvalidateFullCreditApplication } from '@src/data/api/full-credit-application/full-credit-application-api'
import { useValidTerms } from '@src/data/hooks/terms-hooks'
import {
  useBusinessErrors,
  useFeatureFlags,
  useFinancingConfig,
  useProvinceConfigurations,
  useSetBusinessErrors,
} from '@src/data/selectors/app-config-selectors'
import { debounce } from 'lodash-es'
import { nanoid } from 'nanoid'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { Breadcrumb, InputTextField, PageError, PageSpinner, SelectComponent } from '../../../components'
import { useComputeB2cFunding, useUpdateB2cWorksheet } from '../../../data/api/b2c-worksheet/b2c-worksheet-api'
import { useGetMerchantById } from '../../../data/api/merchant/merchant-api'
import { Constants, SelectValueListItem } from '../../../data/types'
import { B2cWorksheet, EditB2cWorksheetDto, EditB2cWorksheetDtoSchema } from '../../../data/types/B2cWorksheetSchema'
import { FundingComputedDto } from '../../../data/types/FundingComputedDto'
import { FundingInfoDto } from '../../../data/types/FundingInfoDto'
import { reportErrorToConsole } from '../../../services/error-logger'
import MerchantPaymentInstructions from '../../ViewCreditApplicationPage/components/MerchantPaymentInstructions'
import {
  useAprTooHigh,
  useEditTotalObligation,
  useFirstPaymentDateOptions,
  usePaymentPlanList,
  usePossibleTerms,
} from '../editWorksheet-hooks'

const EditB2cWorksheetPage = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()
  const featureFlags = useFeatureFlags()

  const { creditApplication } = useCreditApplicationData()

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<EditB2cWorksheetDto>({
    mode: 'onBlur',
    defaultValues: {} as EditB2cWorksheetDto,
    resolver: yupResolver(EditB2cWorksheetDtoSchema),
  })

  const { worksheet, isLoading: isLoadingCreditApplication } = useCreditApplicationData()
  const b2cWorksheet = worksheet as B2cWorksheet | undefined

  const [computedInfo, setComputedInfo] = useState<FundingComputedDto>({} as FundingComputedDto)

  const invalidateFullCreditApplication = useInvalidateFullCreditApplication()
  const [updateWorksheet, isUpdatingWorksheet] = useUpdateB2cWorksheet()
  const [computeFunding, isComputingFunding] = useComputeB2cFunding()

  const { id: creditAppId, financingProgramId } = useCreditApplicationContext()
  const [availableCreditData] = useGetAvailableCredit(financingProgramId, creditAppId)
  const availableCredit = availableCreditData?.availableAmount ?? 0
  const creditInProgress = availableCreditData?.creditInProgress ?? 0
  const isLoading = isUpdatingWorksheet
  const error = useBusinessErrors()
  const setBusinessErrors = useSetBusinessErrors()
  const [merchant] = useGetMerchantById(creditApplication?.merchantId ?? '')

  const totalFees = computedInfo?.lenderFee || 0
  const totalInsurance = (computedInfo?.insuranceFee || 0) + (computedInfo?.insuranceTax || 0)
  const paymentPlanList = usePaymentPlanList(
    creditApplication?.createdOn,
    [],
    new Set<string>(),
    b2cWorksheet?.paymentPlanId,
  )

  const programFinancingConfig = useFinancingConfig(creditApplication?.financingProgramId)
  const [merchantFinancingConfig] = useMerchantFinancingConfig(merchant?.id, creditApplication?.financingProgramId)
  const financingConfig = merchantFinancingConfig ? merchantFinancingConfig : programFinancingConfig

  const possibleTerms = usePossibleTerms(creditApplication?.finalCreditDecision.maxTermDuration, false)

  const provinceConfigs = useProvinceConfigurations()

  const aprTooHigh = useAprTooHigh(computedInfo, creditApplication?.applicant.currentAddress.stateIso, provinceConfigs)

  const firstPaymentOn = watch('firstPaymentOn')

  const easternTime = new Date().toLocaleString('en-US', { timeZone: 'America/Toronto' })
  const currentDateAsEasternTimeZone = new Date(easternTime)

  // dynamic values
  const amountRequested = normalizeNumber(watch('amountRequested')?.toString()) ?? 0
  const paymentFrequency = watch('paymentFrequency') as EPaymentFrequency
  const paymentPlanId = watch('paymentPlanId') as EPaymentPlan
  const deliveryOn = watch('deliveryOn')
  const term = watch('term')
  const includeInsurance = watch('includeInsurance')

  const isNFl =
    creditApplication?.applicant.currentAddress.stateIso &&
    creditApplication.applicant.currentAddress.stateIso === EProvince.Newfoundland
  const highCostRateForProvince = getHighCostRateForProvince(
    provinceConfigs,
    creditApplication?.applicant.currentAddress.stateIso,
  )
  const totalObligation = useEditTotalObligation(amountRequested, computedInfo)
  const planName = creditApplication?.normsCreditDecision?.planName ?? EPlanType.Default

  const maxPaymentAmount = useMaxPaymentAmountWithAgentLeeway(
    paymentPlanId,
    creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
  )
  const [validTerms, invalidTerms, isValidTermsLoading] = useValidTerms({
    creditApplicationId: creditApplication?.id ?? '',
    financingProgramId: creditApplication?.financingProgramId ?? '',
    interestRate: creditApplication?.finalCreditDecision?.interestRate ?? 0,
    hasCoApplicant: Boolean(creditApplication?.coApplicant),
    stateIso: creditApplication?.applicant?.currentAddress?.stateIso ?? '',
    maxPmtAmount: creditApplication?.finalCreditDecision?.maxPmtAmount ?? 0,
    merchantId: merchant?.id ?? '',
    paymentFrequency: b2cWorksheet?.paymentFrequency ?? '',
    paymentPlanId,
    deliveryOn: deliveryOn ? new Date(deliveryOn) : null,
    firstPaymentOn: firstPaymentOn ? new Date(firstPaymentOn) : null,
    amountRequested,
    includeInsurance,
    possibleTerms,
  })

  // Show error banner when selected term is marked as pmtTooHigh
  useEffect(() => {
    const maxPmtAmount = creditApplication?.finalCreditDecision.maxPmtAmount ?? 0

    if (term && invalidTerms.get(term) === 'pmtTooHigh' && paymentFrequency) {
      const errorMsg = t('common.errors.maxPaymentExceedsMaximumAllowed', {
        maxPayment: maxPmtAmount,
      })
      setBusinessErrors([{ message: errorMsg }])
    }
  }, [term, invalidTerms, paymentFrequency, deliveryOn, firstPaymentOn, setBusinessErrors, t, creditApplication?.finalCreditDecision.maxPmtAmount])

  const maxPaymentForFrequencyError = useMaxPaymentError(
    paymentFrequency,
    computedInfo?.paymentForFrequency,
    maxPaymentAmount,
    Constants.financingProgramConfigs[EFinancingProgram.B2c].validateMaxPaymentAmountInWorksheet,
  )

  // reset form on worksheet changed
  useEffect(() => {
    if (b2cWorksheet) {
      reset(b2cWorksheet as EditB2cWorksheetDto)
      setValue('firstPaymentOn', b2cWorksheet.firstPaymentOn ?? new Date())
    }
  }, [b2cWorksheet, merchant, reset, setValue])

  const doComputeFunding = useCallback(
    async (computeDto: FundingInfoDto) => {
      try {
        const data = await computeFunding(computeDto)
        setComputedInfo(data)
      } catch (error) {
        reportErrorToConsole(error)
      }
    },
    [computeFunding],
  )

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceComputeFunding = useCallback(debounce(doComputeFunding, 500), [doComputeFunding])

  useEffect(() => {
    if (creditApplication && id && merchant && b2cWorksheet) {
      const interestRate = creditApplication.finalCreditDecision.interestRate ?? 0
      const stateIso = creditApplication.applicant.currentAddress.stateIso
      const requestId = nanoid()

      const computeDto: FundingInfoDto = {
        financingProgramId: creditApplication?.financingProgramId,
        amountRequested,
        includeInsurance,
        paymentFrequency,
        paymentPlanId,
        term,
        interestRate,
        hasCoApplicant: false,
        stateIso,
        deliveryOn,
        firstPaymentOn,
        requestId,
        creditApplicationId: id,
        merchantId: merchant.id,
        insuranceCompanyId: b2cWorksheet.insuranceCompanyId,
      }
      if (canComputeFunding(computeDto, financingConfig.minimumLoanAmount)) {
        void debounceComputeFunding(computeDto)
      } else {
        const emptyComputedDto = getEmptyComputedFundingDto()
        setComputedInfo(emptyComputedDto)
      }
    }
  }, [
    amountRequested,
    paymentFrequency,
    paymentPlanId,
    deliveryOn,
    firstPaymentOn,
    term,
    includeInsurance,
    creditApplication,
    b2cWorksheet,
    debounceComputeFunding,
    id,
    merchant?.id,
    merchant,
    financingConfig.minimumLoanAmount,
  ])

  const { firstPaymentDateOptions } = useFirstPaymentDateOptions({
    financingProgramId: creditApplication?.financingProgramId,
    creditApplicationId: creditApplication?.id,
    deliveryOn,
    paymentFrequency,
    isHighEstimate: b2cWorksheet?.isHighEstimate ?? false,
    setValue,
    getValues,
  })

  const onFormSubmitted = async (data: EditB2cWorksheetDto) => {
    const expiry = new Date(creditApplication!.expiresOn)
    const receivedOnPlus90Days = new Date(creditApplication!.applicant.hardHitReport.receivedOn)
    receivedOnPlus90Days.setDate(receivedOnPlus90Days.getDate() + 90)
    const deliveryOnDate = new Date(`${getValues('deliveryOn')}T20:00:00`) // 20PM Local Time
    const deliveryOnUTC = new Date(`${getValues('deliveryOn')}T00:00:00`)
    const deliveryOnDateAsEasternTimeZone = new Date(
      deliveryOnDate.toLocaleString('en-US', { timeZone: 'America/Toronto' }),
    )
    const baseActivationDateAsEasternTimeZone = new Date(
      currentDateAsEasternTimeZone.getFullYear(),
      currentDateAsEasternTimeZone.getMonth(),
      currentDateAsEasternTimeZone.getDate(),
      0,
      0,
      0,
    )
    if (
      isNFl
        ? computedInfo.effectiveRate > highCostRateForProvince
        : computedInfo.effectiveRate > financingConfig.effectiveRateThreshold
    ) {
      setBusinessErrors([
        {
          message: t('worksheetCommon.effectiveRateTooHigh', '', {
            effectiveRate: computedInfo.effectiveRate,
            maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
          }),
        },
      ])
    } else if (compareAsc(deliveryOnDateAsEasternTimeZone, baseActivationDateAsEasternTimeZone) === -1) {
      setBusinessErrors([{ message: 'worksheetCommon.activationDateInPast' }])
    } else if (amountRequested > availableCredit) {
      setBusinessErrors([{ message: 'worksheetCommon.amountRequestedHigherThanAvailableCredit' }])
    } else if (compareAsc(deliveryOnUTC, receivedOnPlus90Days) === 1)
      setBusinessErrors([{ message: 'worksheetCommon.activationDateReport90Days' }])
    else if (compareAsc(deliveryOnUTC, expiry) === 1)
      setBusinessErrors([{ message: 'worksheetCommon.deliveryOnEarlierExpiration' }])
    else {
      data.amountRequested = amountRequested
      data.status = EWorksheetStatus.Active
      try {
        await updateWorksheet(data)
        await invalidateFullCreditApplication(creditApplication!.id, creditApplication!.financingProgramId)
        navigate(`/Applications/${EFinancingProgram.B2c}/${id}/view`, { replace: true })
      } catch (error) {
        reportErrorToConsole(error)
      }
    }
  }

  const breadCrumbs = useMemo(
    () => [
      { path: '/', label: t('breadcrumbs.home') },
      { path: '/Applications/browse', label: t('breadcrumbs.creditApplication') },
      {
        path: `/Applications/${EFinancingProgram.B2c}/${creditApplication?.id}/view`,
        label: t('breadcrumbs.application').concat(` #${creditApplication?.referenceNumber}`),
      },
      { path: '#', label: t('worksheet.financing') },
    ],
    [creditApplication, t],
  )

  return (
    <div>
      <PageSpinner withBackdrop isLoading={isLoading && isLoadingCreditApplication} />
      {!isLoading && (
        <Stack>
          <PageError errors={error} />
          {featureFlags.fixPersonalLoanVariableTermLoop &&
            creditApplication &&
            computedInfo &&
            computedInfo.effectiveRate > (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold) && (
              <Alert severity="warning">
                {t('worksheetCommon.effectiveRateTooHigh', '', {
                  effectiveRate: computedInfo.effectiveRate,
                  maxRate: isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold,
                })}
              </Alert>
            )}
        </Stack>
      )}
      <Breadcrumb trees={breadCrumbs} />
      <form onSubmit={handleSubmit(onFormSubmitted, reportErrorToConsole)}>
        <Stack direction="row" spacing={2} justifyContent="space-between">
          <InputTextField value={`${planName}`} label={t('worksheetCommon.program')} disabled />
          <InputTextField
            label={t('worksheetCommon.creditLimit')}
            disabled
            value={creditApplication?.finalCreditDecision?.maxAmountFinanced ?? 0}
          />
          <InputTextField label={t('worksheetCommon.creditInProgress')} disabled value={creditInProgress} />
          <InputTextField label={t('worksheetCommon.availableCredit')} disabled value={availableCredit} />
          <div style={{ marginTop: '1%' }}>
            <Button
              variant="contained"
              type="submit"
              disabled={
                aprTooHigh ||
                isComputingFunding ||
                !computedInfo ||
                isLoading ||
                maxPaymentForFrequencyError !== null ||
                (term && invalidTerms.get(term) === 'pmtTooHigh') ||
                (computedInfo &&
                  computedInfo.effectiveRate >
                    (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold))
              }
            >
              {t('common.save')}
            </Button>
          </div>
        </Stack>
        {merchant && <MerchantPaymentInstructions merchant={merchant} />}
        <Grid container marginTop={5}>
          <Grid size={8}>
            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <InputTextField
                  slotProps={{
                    input: {
                      endAdornment: '$',
                    },
                  }}
                  {...register('amountRequested')}
                  error={errors?.amountRequested}
                  label={t('worksheetCommon.amountRequested')}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <ControlledStringDateOnlyPicker
                  name="deliveryOn"
                  control={control}
                  label={t('worksheetCommon.activationDate')}
                  disablePast
                  minDate={currentDateAsEasternTimeZone}
                  disabled={creditApplication?.editLocked}
                  fullWidth
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={firstPaymentDateOptions.map((item) => {
                    const data: SelectValueListItem = {
                      label: formatDate(item),
                      value: formatDate(item),
                    }
                    return data
                  })}
                  label={t('worksheetCommon.firstPaymentDate')}
                  {...register('firstPaymentOn')}
                  error={errors?.firstPaymentOn}
                  value={firstPaymentOn ? formatDate(firstPaymentOn) : ''}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={paymentPlanList}
                  label={t('worksheet.paymentPlan')}
                  {...register('paymentPlanId')}
                  error={errors?.paymentPlanId}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <SelectComponent
                  items={[{ label: t('EPaymentFrequency.monthly', { ns: 'enum' }), value: EPaymentFrequency.Monthly }]}
                  label={t('worksheet.paymentFrequency')}
                  {...register('paymentFrequency')}
                  error={errors?.paymentFrequency}
                />
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                {isValidTermsLoading ? (
                  <Stack alignItems="center" justifyContent="center" sx={{ height: 56 }}>
                    <CircularProgress size={24} />
                  </Stack>
                ) : (
                  <SelectComponent
                    items={possibleTerms.map((item) => {
                      const isInvalid = featureFlags.fixPersonalLoanVariableTermLoop && !validTerms.has(item)
                      let invalidReason = ''
                      if (isInvalid) {
                        const reason = invalidTerms.get(item)
                        const pmtTooHigh = reason === 'pmtTooHigh'
                        const aprTooHigh = computedInfo?.effectiveRate > (isNFl ? highCostRateForProvince : financingConfig.effectiveRateThreshold)

                        if (pmtTooHigh && aprTooHigh) {
                          invalidReason = t('worksheetCommon.PMTAPRTooHigh')
                        } else if (pmtTooHigh) {
                          invalidReason = t('worksheetCommon.PMTTooHigh')
                        } else {
                          invalidReason = t('worksheetCommon.TCATooHigh')
                        }
                      }
                      return {
                        label: featureFlags.fixPersonalLoanVariableTermLoop
                          ? `${item} ${t('worksheet.month')}${isInvalid ? ` (${invalidReason})` : ''}`
                          : `${item} ${t('worksheet.month')}`,
                        value: item,
                        disabled: isInvalid,
                      }
                    })}
                    label={t('worksheet.term')}
                    {...register('term')}
                    error={errors?.term}
                  />
                )}
              </Grid>
              <Grid
                size={{
                  xs: 6,
                  md: 4,
                }}
              >
                <FormControlLabel
                  {...register('includeInsurance')}
                  control={<Checkbox defaultChecked={b2cWorksheet?.includeInsurance} />}
                  label={t('worksheetCommon.addProtectionPlan')}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid size={4}>
            <Box
              sx={{
                border: 1,
                padding: 2,
                marginLeft: 5,
                paddingBottom: 10,
              }}
            >
              {isComputingFunding ? (
                <Stack justifyContent="center" alignItems="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </Stack>
              ) : (
                <Stack direction="column" spacing={2}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {t('worksheetCommon.fundingSummary')} :
                  </Typography>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.loanAmount')} :</Typography>
                    <Typography>{`${formatCurrency(getValues('amountRequested'))}`}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>
                      {`${t('worksheetCommon.interest')} (${`${creditApplication?.finalCreditDecision?.interestRate}%`})`}
                      :{' '}
                    </Typography>
                    <Typography>{formatCurrency(computedInfo?.totalInterestAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{`${t('worksheetCommon.financeFee')} (${computedInfo?.lenderFeeRate}%)`}:</Typography>
                    <Typography>{formatCurrency(totalFees)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.protectionPlan')} :</Typography>
                    <Typography>{formatCurrency(totalInsurance)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.totalObligation')} :</Typography>
                    <Typography>{formatCurrency(totalObligation)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.annualPercentageRate')} :</Typography>
                    <Typography>{computedInfo.effectiveRate} %</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {t('worksheetCommon.payment')}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.maxPmtAmount')} :</Typography>
                    <Typography>{formatCurrency(creditApplication?.finalCreditDecision.maxPmtAmount)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.paymentFrequency')} :</Typography>
                    <Typography>
                      {b2cWorksheet?.paymentFrequency
                        ? t(`EPaymentFrequency.${b2cWorksheet.paymentFrequency}`, '', { ns: 'enum' })
                        : t('EPaymentFrequency.monthly', '', { ns: 'enum' })}
                    </Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.frequencyAmount')} :</Typography>
                    <Typography>{formatCurrency(computedInfo?.paymentForFrequency)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between">
                    <Typography>{t('worksheetCommon.firstPaymentDate')} :</Typography>
                    <Typography>{formatDate(firstPaymentOn)}</Typography>
                  </Stack>

                  <Stack direction="row" justifyContent="space-between" paddingTop={3}>
                    <Typography fontWeight="bold">{t('worksheetCommon.amountToBePaid')} :</Typography>
                    <Typography>{formatCurrency(getValues('amountRequested') ?? 0)}</Typography>
                  </Stack>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </form>
    </div>
  )
}

export default EditB2cWorksheetPage
